/**
 * ============================================================================
 *  useVoiceInput — 语音输入核心 Hook
 * ============================================================================
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 📌 职责
 * ──────────────────────────────────────────────────────────────────────────
 *  本 Hook 封装整条语音采集 → 传输 → 识别管道：
 *    getUserMedia → AudioContext → ScriptProcessorNode → Int16 PCM
 *    → WebSocket(/api/voice/ws) → voiceProxy → 火山引擎 ASR
 *    → transcript 文本 → onTranscript 回调
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 🔄 完整调用流程
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  1. 调用方（VoiceMicButton / useVoiceShortcut）调用 startRecording()
 *  2. 获取麦克风权限（getUserMedia，16kHz 单声道）
 *  3. 创建 AudioContext + ScriptProcessorNode（bufferSize=4096）
 *     ⚠️ ScriptProcessorNode 已被标记为 deprecated，但目前仍是兼容性最好的方案
 *        未来可迁移到 AudioWorkletNode
 *  4. 建立到后端的 WebSocket 连接（/api/voice/ws?token=JWT）
 *  5. WebSocket open 后发送 { type: 'start' } 控制命令
 *  6. 后端 voiceProxy 建立到火山引擎的上游 WSS 连接
 *  7. 音频帧（Int16 PCM）通过 ScriptProcessorNode.onaudioprocess 实时发送
 *  8. 火山引擎返回中间 / 最终识别结果 → voiceProxy 转为 JSON 发回前端
 *  9. 调用方松开按键 → stopRecording() 发送 { type: 'stop' }
 * 10. 后端发送最后一帧音频给火山引擎，等待最终结果
 * 11. 收到 isLast=true 的 transcript → 关闭 WebSocket → 录音结束
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 🛡️ 会话管理（防止多次快速按键互相干扰）
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  每次 startRecording() 递增 sessionIdRef（会话代数）。
 *  所有异步回调（WebSocket onmessage/onclose）先判断
 *  wsRef.current === thisWs，确保只处理当前会话的消息。
 *
 *  安全超时（10 秒）绑定到 wsToGuard（当时的 ws 引用），
 *  不会误关后续新开的连接。
 *
 * ──────────────────────────────────────────────────────────────────────────
 * 🎧 音频处理细节
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  - 目标采样率 16kHz（火山引擎要求）
 *  - audioContext sampleRate 可能不等于 16kHz（浏览器硬件依赖）
 *    → 差异时用线性插值重采样（浮点数层面）
 *  - Float32 → Int16 转换后发送原始 PCM ArrayBuffer（非 JSON）
 *  - 用 GainNode(0) 静音防止扬声器回声
 *
 * 使用方式：
 *   const { isRecording, transcript, startRecording, stopRecording } = useVoiceInput({
 *     onTranscript: (text, isFinal) => { ... },
 *     onError: (msg) => { ... },
 *   });
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../constants';
import { getToken } from '../api';

/**
 * 将 Float32 采样缓冲区转为 Int16 PCM（小端序）
 * @param {Float32Array} float32Arr
 * @returns {Int16Array}
 */
function float32ToInt16(float32Arr) {
  const len = float32Arr.length;
  const int16 = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, float32Arr[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16;
}

/**
 * @typedef {Object} VoiceInputOptions
 * @property {(text: string, isFinal: boolean) => void} [onTranscript] - 识别结果回调
 * @property {(msg: string) => void} [onError] - 错误回调
 */

/**
 * @param {VoiceInputOptions} options
 */
export default function useVoiceInput(options = {}) {
  const { onTranscript, onError } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');

  // refs（在录音过程中保持稳定引用）
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const isRecordingRef = useRef(false);

  /** 安全超时句柄，防止旧超时关闭新连接 */
  const safetyTimeoutRef = useRef(null);

  /** 会话代数，每次 startRecording 递增，用于避免跨会话干扰 */
  const sessionIdRef = useRef(0);

  /** 上一次日志的识别文本，用于去重 */
  const lastLoggedTextRef = useRef('');

  // 缓存回调（避免闭包陈旧）
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  /** 清除安全超时 */
  const clearSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  /**
   * 清理音频资源（不关闭 WebSocket）
   */
  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  /**
   * 清理所有资源（音频 + WebSocket + 状态）
   */
  const cleanup = useCallback(() => {
    isRecordingRef.current = false;
    setIsRecording(false);
    clearSafetyTimeout();
    cleanupAudio();

    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* safe */ }
      wsRef.current = null;
    }
  }, [clearSafetyTimeout, cleanupAudio]);

  /**
   * 开始录音
   *
   * 完整流程：
   *   1. 清理上一次残留的超时 & WebSocket
   *   2. 获取麦克风权限
   *   3. 建立 WebSocket 到后端 voiceProxy
   *   4. 等待 ASR 就绪后开始发送音频
   */
  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) return;

    // ── 0. 递增会话 ID，清理上一轮残留 ──
    const currentSession = ++sessionIdRef.current;
    clearSafetyTimeout();
    lastLoggedTextRef.current = '';

    // 如果上一轮 WebSocket 还没关闭，立即关闭
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* safe */ }
      wsRef.current = null;
    }

    // ── 1. 立即切换为"录音中"状态 ──
    isRecordingRef.current = true;
    setIsRecording(true);
    setTranscript('');

    // ── 2. 检查安全上下文 ──
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      onErrorRef.current?.('当前环境不支持麦克风（需要 HTTPS 或 localhost）');
      cleanup();
      return;
    }

    // ── 3. 获取麦克风权限 ──
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? '请允许麦克风权限以使用语音输入'
        : '无法访问麦克风: ' + err.message;
      onErrorRef.current?.(msg);
      cleanup();
      return;
    }
    // 如果在 await 期间用户已松开（会话过期），立即释放
    if (sessionIdRef.current !== currentSession) {
      stream.getTracks().forEach(t => t.stop());
      return;
    }
    mediaStreamRef.current = stream;

    // ── 4. 创建 AudioContext（目标 16kHz） ──
    let audioCtx;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    } catch (e) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    audioContextRef.current = audioCtx;

    const source = audioCtx.createMediaStreamSource(stream);
    const actualRate = audioCtx.sampleRate;
    const needResample = actualRate !== 16000;

    // ScriptProcessorNode（浏览器已标记 deprecated，但功能仍正常）
    // TODO: 未来可迁移到 AudioWorkletNode
    // bufferSize=1024: 64ms/帧 @ 16kHz，比 4096(256ms) 更频繁地推送给 ASR，降低首字延迟
    const bufferSize = 1024;
    const processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
    processorRef.current = processor;

    // 用 GainNode(0) 静音，防止扬声器播放麦克风音频
    const muteGain = audioCtx.createGain();
    muteGain.gain.value = 0;
    processor.connect(muteGain);
    muteGain.connect(audioCtx.destination);

    processor.onaudioprocess = (e) => {
      if (!isRecordingRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

      const inputData = e.inputBuffer.getChannelData(0);

      let pcmFloat;
      if (needResample) {
        const ratio = 16000 / actualRate;
        const newLen = Math.floor(inputData.length * ratio);
        pcmFloat = new Float32Array(newLen);
        for (let i = 0; i < newLen; i++) {
          const srcIdx = i / ratio;
          const lo = Math.floor(srcIdx);
          const hi = Math.min(lo + 1, inputData.length - 1);
          const frac = srcIdx - lo;
          pcmFloat[i] = inputData[lo] * (1 - frac) + inputData[hi] * frac;
        }
      } else {
        pcmFloat = inputData;
      }

      const int16 = float32ToInt16(pcmFloat);
      wsRef.current.send(int16.buffer);
    };

    source.connect(processor);

    // ── 5. 建立 WebSocket 连接到后端代理 ──
    const token = getToken();
    if (!token) {
      onErrorRef.current?.('请先登录后再使用语音输入');
      cleanup();
      return;
    }

    const wsProtocol = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_BASE_URL.replace(/^https?/, wsProtocol);
    const wsUrl = `${wsHost}/api/voice/ws?token=${encodeURIComponent(token)}`;
    console.log('🎤 [Voice] 开始录音，连接 ASR...');

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      onErrorRef.current?.('无法创建语音连接');
      cleanup();
      return;
    }
    wsRef.current = ws;

    // 捕获当前 ws 引用，所有回调用它判断是否还是当前会话
    const thisWs = ws;

    ws.onopen = () => {
      if (wsRef.current !== thisWs) return; // 已被新会话替换
      ws.send(JSON.stringify({ type: 'start' }));
    };

    ws.onmessage = (event) => {
      if (wsRef.current !== thisWs && !isRecordingRef.current) return;
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'ready') {
          console.log('🎤 [Voice] ASR 就绪');
        }

        if (msg.type === 'transcript') {
          const text = msg.text || '';
          // 只在文本变化时更新和记录日志（去重）
          if (text !== lastLoggedTextRef.current) {
            lastLoggedTextRef.current = text;
            if (text) console.log('🎤 [Voice]', msg.isLast ? '✅' : '…', text);
          }
          setTranscript(text);
          onTranscriptRef.current?.(text, msg.isLast);

          if (msg.isLast) {
            console.log('🎤 [Voice] 识别完成');
            clearSafetyTimeout();
            if (wsRef.current === thisWs) {
              try { wsRef.current.close(); } catch (e) { /* safe */ }
              wsRef.current = null;
            }
          }
        }

        if (msg.type === 'error') {
          console.error('🎤 [Voice] 错误:', msg.message);
          onErrorRef.current?.(msg.message || '语音识别错误');
          if (wsRef.current === thisWs) cleanup();
        }
      } catch (e) { /* 非 JSON，忽略 */ }
    };

    ws.onerror = () => {
      if (wsRef.current !== thisWs) return;
      onErrorRef.current?.('语音服务连接失败，请检查语音识别配置');
      cleanup();
    };

    ws.onclose = () => {
      // 只有当前会话的 ws 关闭才需要处理
      if (wsRef.current === thisWs && isRecordingRef.current) {
        console.warn('🎤 [Voice] 连接异常断开');
        cleanup();
      }
    };

  }, [cleanup, clearSafetyTimeout, cleanupAudio]);

  /**
   * 停止录音
   *
   * 发送 stop 命令后保持 WebSocket 等待最终结果。
   * 10 秒安全超时防止永久等待（仅关闭当前 ws，不影响后续新连接）。
   */
  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;
    console.log('🎤 [Voice] 停止录音，等待结果...');

    // 发送停止命令
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
    }

    // 停止音频采集，但保持 WebSocket 打开等待最终结果
    isRecordingRef.current = false;
    setIsRecording(false);
    cleanupAudio();

    // 安全超时：10 秒后强制关闭（仅关闭当前会话的 ws）
    clearSafetyTimeout();
    const wsToGuard = wsRef.current; // 捕获当前 ws 引用
    safetyTimeoutRef.current = setTimeout(() => {
      // 只关闭"当时"的 ws，不影响后续新连接
      if (wsRef.current === wsToGuard && wsToGuard) {
        console.log('🎤 [Voice] 超时，关闭连接');
        try { wsToGuard.close(); } catch (e) { /* safe */ }
        if (wsRef.current === wsToGuard) wsRef.current = null;
      }
      safetyTimeoutRef.current = null;
    }, 10000);
  }, [cleanupAudio, clearSafetyTimeout]);

  // 组件卸载时清理
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    isRecording,
    transcript,
    startRecording,
    stopRecording,
  };
}
